<template>
  <div class="custom-ui-image-cropper-wrapper">
    <div class="custom-ui-image-cropper-label-row" v-if="label || info">
      <div class="custom-ui-image-cropper-label" v-if="label" v-text="label" />
      <ui-field-info :text="info" />
    </div>
    <div class="custom-ui-image-dropper-options">
      <div class="cropper-download" v-if="cDownload && imageUrl && !uploadedFile">
        <i class="fa-solid fa-download" @click="downloadImage" />
      </div>
      <span class="dot-divider" v-if="cDownload && imageUrl && !uploadedFile && cSizeHint" />
      <div class="cropper-hint" v-if="cSizeHint">
        <i class="fa-light fa-expand-wide" />
        <span v-text="cSizeHint" />
      </div>
    </div>
    <ui-line-message
      v-if="resolutionWarning"
      size="small"
      class="mb05"
      :message="resolutionWarning"
      iconType="warning"
      icon="fa-light fa-triangle-exclamation"
    />
    <div class="custom-ui-image-cropper" :class="[style, disabled ? 'disabled' : '']">
      <file-pond
        v-if="!uploadedFile"
        ref="pond"
        :disabled="disabled"
        class="file-pond-style"
        name="test"
        :label-idle="t('admin_global_dropfileshere') + '...'"
        :allow-multiple="false"
        :accepted-file-types="cAccept"
        v-bind:files="imageUrl"
        v-on:addfile="selectedFile"
        v-on:removefile="removeFile"
      />
      <div class="cropper-area" v-if="!image && uploadedFile">
        <Cropper
          ref="cropper"
          :stencil-props="{
            aspectRatio: cAspectRatio,
          }"
          :src="uploadedFile.src"
          :min-height="cMinHeight"
          :min-width="cMinWidth"
          class="cropperclass"
          :default-size="defaultSize"
        />
        <ui-button
          type="secondary"
          :label="t('admin_global_crop')"
          @click="crop"
          class="mt05"
          expanded
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
  import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
  import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
  import 'filepond/dist/filepond.min.css'
  import vueFilePond from 'vue-filepond'

  import type { IUIImageCropper, UIImageCropperExpose } from '@/types/Components'
  import { Alert, UseI18n } from '@/utils-ts'
  import { cropperSpec } from '@/utils-ts/ImageCropper'
  import ImageBlobReduce from 'image-blob-reduce'
  import { computed, onMounted, ref, watch } from 'vue'
  import { Cropper } from 'vue-advanced-cropper'
  import 'vue-advanced-cropper/dist/style.css'

  const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview)
  const reduce = ImageBlobReduce()

  /** MB — raw upload guard, so a huge file never freezes the browser before cropping. */
  const UPLOAD_LIMIT_MB = 25
  /** Fields without an output size or MB cap still get downscaled past this size. */
  const SAFETY_LIMIT_MB = 3
  const SAFETY_OUTPUT_SIZE = 2048
  /** Ladders walked to squeeze a crop under its cap instead of rejecting it. */
  const COMPRESSION_QUALITIES = [0.85, 0.7, 0.55]
  const COMPRESSION_SCALES = [1, 0.75, 0.5, 0.35]

  const {
    label = '',
    disabled = false,
    spec = undefined,
    aspectRatio = undefined,
    anyError = false,
    hasValidation = false,
    outputSize = undefined,
    maxSizeMB = undefined,
    accept = undefined,
    minHeight = undefined,
    minWidth = undefined,
    isMultiple = undefined,
    info = '',
    recommended = '',
    download = undefined,
  } = defineProps<IUIImageCropper>()

  // Named spec from src/data/imageCropperSpecs.json. Explicit props always win
  // over the spec; the spec wins over the built-in fallbacks.
  const resolvedSpec = computed(() => (spec ? cropperSpec(spec) : {}))
  const cAspectRatio = computed(() => aspectRatio ?? resolvedSpec.value.aspectRatio ?? null)
  const cMinWidth = computed(() => minWidth ?? resolvedSpec.value.minWidth ?? 10)
  const cMinHeight = computed(() => minHeight ?? resolvedSpec.value.minHeight ?? 10)
  const cOutputSize = computed(() => outputSize ?? resolvedSpec.value.outputSize)
  const cRecommended = computed(() => recommended || resolvedSpec.value.recommended || '')
  const cMaxSizeMB = computed(() => maxSizeMB ?? resolvedSpec.value.maxSizeMB)
  const cAccept = computed(() => accept ?? resolvedSpec.value.accept ?? 'image/jpeg, image/png')
  const cDownload = computed(() => download ?? resolvedSpec.value.download ?? true)
  const cIsMultiple = computed(() => isMultiple ?? resolvedSpec.value.isMultiple ?? false)
  const cSizeHint = computed(() => {
    const base = cRecommended.value
    if (!cMaxSizeMB.value) return base
    const sizeInfo = `${t('admin_global_image_max_size_info', { size: cMaxSizeMB.value })}`
    return base ? `${base} · ${sizeInfo}` : sizeInfo
  })

  const model = defineModel<string | Blob>({ required: true })
  const emit = defineEmits<{
    (e: 'crop'): void
    (e: 'remove'): void
  }>()

  const { t } = UseI18n()
  const cropper = ref<any>(null)
  const pond = ref<any>(null)

  const uploadedFile = ref<{ type: string; src: any } | null>(null)
  const image = ref<string | Blob>('')
  const imageUrl = ref<string>('')
  const isCropped = ref<boolean>(true)
  const resolutionWarning = ref<string>('')

  const style = computed(() => {
    if (hasValidation) {
      return anyError ? 'danger' : 'success'
    } else {
      return ''
    }
  })

  watch(model, (val: string | Blob) => {
    if (val) {
      if (val instanceof Blob) {
        imageUrl.value = URL.createObjectURL(val)
      } else {
        imageUrl.value = val
      }
    }
  })

  onMounted(() => {
    if (model.value) {
      if (model.value instanceof Blob) {
        imageUrl.value = URL.createObjectURL(model.value)
      } else if (typeof model.value === 'string') {
        imageUrl.value = model.value
      }
    }
  })

  /** Resource strings live on the API; fall back to Turkish until the keys land. */
  const message = (key: string, fallback: string, params: Record<string, string | number>) => {
    const translated = t(key, params)
    if (translated !== key) return translated
    return Object.entries(params).reduce(
      (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
      fallback,
    )
  }

  const checkResolution = (src: string) => {
    resolutionWarning.value = ''
    const [width, height] = [cMinWidth.value, cMinHeight.value]
    if (width <= 10 && height <= 10) return
    const probe = new Image()
    probe.onload = () => {
      if (probe.naturalWidth >= width && probe.naturalHeight >= height) return
      resolutionWarning.value = message(
        'admin_global_image_low_resolution',
        'Bu görsel önerilen {size} ölçüsünün altında, yayında bulanık görünebilir.',
        { size: `${width} × ${height} px` },
      )
    }
    probe.src = src
  }

  const selectedFile = (error: any, file: any) => {
    if (error || imageUrl.value) return
    const source: File = file.file
    if (source.size > UPLOAD_LIMIT_MB * 1024 * 1024) {
      Alert({
        type: 'danger',
        message: message(
          'admin_global_image_upload_limit_exceeded',
          'Dosya çok büyük ({size} MB). Lütfen {limit} MB altında bir görsel seçin.',
          { size: (source.size / 1024 / 1024).toFixed(1), limit: UPLOAD_LIMIT_MB },
        ),
      })
      pond.value?.removeFile(file.id)
      return
    }
    const reader = new FileReader()
    reader.onload = e => {
      const src = e.target?.result
      uploadedFile.value = { type: source.type, src }
      checkResolution(src as string)
    }
    reader.readAsDataURL(source)
    isCropped.value = false
  }

  const croppedImage = (img: Blob) => {
    imageUrl.value = URL.createObjectURL(img)
    image.value = img
    uploadedFile.value = null
    resolutionWarning.value = ''
    model.value = img
    emit('crop')
    isCropped.value = true
    if (cIsMultiple.value) {
      image.value = ''
      imageUrl.value = ''
      model.value = ''
    }
  }

  const defaultSize = ({ imageSize, visibleArea }: any) => {
    return {
      width: (visibleArea || imageSize).width,
      height: (visibleArea || imageSize).height,
    }
  }

  const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) =>
    new Promise<Blob | null>(resolve => canvas.toBlob(blob => resolve(blob), type, quality))

  /** Re-encoding to JPEG is only safe when the crop has no see-through pixels. */
  const hasTransparency = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d')
    if (!context) return true
    const { data } = context.getImageData(0, 0, canvas.width, canvas.height)
    for (let i = 3; i < data.length; i += 4) {
      if (data[i]! < 255) return true
    }
    return false
  }

  const scaleCanvas = (source: HTMLCanvasElement, scale: number) => {
    const target = document.createElement('canvas')
    target.width = Math.max(1, Math.round(source.width * scale))
    target.height = Math.max(1, Math.round(source.height * scale))
    const context = target.getContext('2d')
    if (context) {
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = 'high'
      context.drawImage(source, 0, 0, target.width, target.height)
    }
    return target
  }

  /** Scale the crop down to the spec's delivery size; framing itself stays free. */
  const resizeToOutput = async (canvas: HTMLCanvasElement, type: string, size: number) => {
    if (Math.max(canvas.width, canvas.height) <= size) return canvas
    const full = await canvasToBlob(canvas, type)
    return full ? ((await reduce.toCanvas(full, { max: size })) as HTMLCanvasElement) : canvas
  }

  /**
   * Walk down quality, then resolution, until the crop fits its cap. Admins should
   * never reach a dead end, so the smallest attempt is kept as a last resort.
   */
  const fitToLimit = async (canvas: HTMLCanvasElement, limit: number, transparent: boolean) => {
    const type = transparent ? 'image/png' : 'image/jpeg'
    const qualities = transparent ? [undefined] : COMPRESSION_QUALITIES
    let smallest: Blob | null = null
    for (const scale of COMPRESSION_SCALES) {
      const target = scale === 1 ? canvas : scaleCanvas(canvas, scale)
      for (const quality of qualities) {
        const blob = await canvasToBlob(target, type, quality)
        if (!blob) continue
        if (blob.size <= limit) return blob
        if (!smallest || blob.size < smallest.size) smallest = blob
      }
    }
    return smallest
  }

  const crop = async () => {
    const { canvas } = cropper.value.getResult()
    if (!canvas) return

    canvas.imageSmoothingEnabled = true
    canvas.imageSmoothingQuality = 'low'

    const type = uploadedFile.value?.type || 'image/jpeg'
    const limit = cMaxSizeMB.value ? cMaxSizeMB.value * 1024 * 1024 : undefined
    const outputSizePx = cOutputSize.value

    let source: HTMLCanvasElement = outputSizePx
      ? await resizeToOutput(canvas, type, outputSizePx)
      : canvas
    let result = await canvasToBlob(source, type)
    if (!result) return

    // Neither a delivery size nor a cap: keep the long-standing oversize safety net.
    if (!outputSizePx && !limit && result.size > SAFETY_LIMIT_MB * 1024 * 1024) {
      source = await resizeToOutput(source, type, SAFETY_OUTPUT_SIZE)
      result = (await canvasToBlob(source, type)) ?? result
    }

    if (limit && result.size > limit) {
      const transparent = type === 'image/png' && hasTransparency(source)
      const fitted = await fitToLimit(source, limit, transparent)
      if (fitted) result = fitted
    }

    if (limit && result.size > limit) {
      Alert({
        type: 'danger',
        message: t('admin_global_image_size_limit_exceeded', { size: cMaxSizeMB.value }),
      })
      return
    }

    croppedImage(result)
  }

  const downloadFileName = () => {
    if (model.value instanceof Blob) {
      const ext = model.value.type.split('/')[1]
      return ext ? `image.${ext}` : 'image'
    }
    const name = imageUrl.value.split('/').pop()?.split('?')[0]
    return name || 'image'
  }

  const downloadImage = async () => {
    if (!imageUrl.value) return
    let url = imageUrl.value
    let objectUrl = ''
    try {
      // Remote URLs must go through a blob so the download attribute is honored.
      if (!url.startsWith('blob:') && !url.startsWith('data:')) {
        const response = await fetch(url)
        objectUrl = URL.createObjectURL(await response.blob())
        url = objectUrl
      }
      const link = document.createElement('a')
      link.href = url
      link.download = downloadFileName()
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error(error)
      Alert({
        type: 'danger',
        message: t('admin_global_error'),
      })
    } finally {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }

  const removeFile = () => {
    if (disabled) return
    image.value = ''
    imageUrl.value = ''
    resolutionWarning.value = ''
    model.value = ''
    emit('remove')
  }

  const cropAlert = () => {
    Alert({
      type: 'danger',
      message: t('admin_global_please_crop_image'),
    })
  }

  defineExpose<UIImageCropperExpose>({
    isCropped,
    cropAlert,
  })
</script>

<style lang="scss">
  .custom-ui-image-cropper-wrapper {
    width: 100%;
    height: auto;

    .custom-ui-image-cropper-label-row {
      @include flexible($align: center, $wrap: wrap);
      gap: 0.35rem;
      margin-bottom: 0.5rem;
      .custom-ui-image-cropper-label {
        @include column-label($margin: 0);
        width: auto;
      }
    }
    .custom-ui-image-dropper-options {
      @include flexible($align: center);
      gap: 0.35rem;
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.7;
      .cropper-download {
        cursor: pointer;
      }

      .dot-divider {
        width: 2px;
        height: 2px;
        background-color: $primary-text-color;
      }
    }
    .custom-ui-image-cropper-hint {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      margin-bottom: 0.5rem;
      font-size: 0.75rem;
      opacity: 0.7;
    }
    .custom-ui-image-cropper {
      border-radius: 5px;
      a.filepond--credits {
        display: none !important;
      }
      .filepond--root {
        margin-bottom: 0 !important;
      }
      &.danger {
        border: 1px solid $danger;
      }
      &.success {
        border: 1px solid $success;
      }
      &.disabled {
        cursor: not-allowed;
      }
    }
  }
</style>
